import React from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
} from '@headlessui/react';
import { BreakEvent, DaySequence } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { formatBreakTime, formatTime } from '@/utils/utils';
import Button from '@/components/Button';

/**
 * Confirmation step before committing a timesheet action.
 *
 * Read-only summary of the final clock-in, clock-out, and break times. Used for:
 * - Approve: opened directly from JobTimesheets when the user approves logged times.
 * - Resolve: opened as step 2 inside EditTimesheetModal after the user adjusts disputed times.
 *
 * See EditTimesheetModal for the editable form that precedes resolve confirmation.
 */
interface UpdateTimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  updateTimesheet: DaySequence | null;
  finalClockInTime: string; // Unix timestamp in milliseconds
  finalClockOutTime: string; // Unix timestamp in milliseconds
  finalBreakTime: BreakEvent[] | null;
  /** Which action this confirmation precedes. */
  confirmationVariant?: 'approve' | 'resolve' | 'propose';
  onApproveTimesheet?: (daySequenceId: string) => void;
  onResolveTimesheet?: (daySequenceId: string) => void;
  onProposeTimesheet?: (daySequenceId: string) => void;
}

const UpdateTimesheetModal = ({
  isOpen,
  onClose,
  updateTimesheet,
  finalClockInTime,
  finalClockOutTime,
  finalBreakTime,
  confirmationVariant = 'approve',
  onApproveTimesheet,
  onResolveTimesheet,
  onProposeTimesheet,
}: UpdateTimesheetModalProps) => {
  const { getWorkerById } = useWorkerStore();
  const getJobById = useJobStore((state) => state.getJobById);
  const worker = getWorkerById(updateTimesheet?.userId || '');
  const job = updateTimesheet?.jobId ? getJobById(updateTimesheet.jobId) : null;

  const finalClockInTimeString = finalClockInTime
    ? formatTime(finalClockInTime, job)
    : null;
  const finalClockOutTimeString = finalClockOutTime
    ? formatTime(finalClockOutTime, job)
    : null;
  const finalBreakTimeString = finalBreakTime
    ? formatBreakTime(finalBreakTime)
    : '0 mins';

  const confirmationCopy = (() => {
    switch (confirmationVariant) {
      case 'propose':
        return {
          title: 'propose edited times for',
          action: 'Propose timesheet changes',
          description:
            'Proposed times will be sent to the candidate for confirmation.',
        };
      case 'resolve':
        return {
          title: 'dispute',
          action: 'Resolve timesheet',
          description: 'This action will affect their final payment.',
        };
      default:
        return {
          title: 'approve',
          action: 'Approve timesheet',
          description: 'This action will affect their final payment.',
        };
    }
  })();

  if (!updateTimesheet) {
    return null;
  }

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-60" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white px-6 py-12 text-left align-middle shadow-xl transition-all">
                <DialogTitle
                  as="h3"
                  className="flex items-center justify-center text-lg leading-6 font-medium text-gray-900"
                >
                  {`Do you want to ${confirmationCopy.title} the timesheet${worker ? ` for ${worker?.firstName} ${worker?.lastName}` : ''}?`}
                </DialogTitle>
                <div className="mt-2 mb-5 flex flex-col items-center justify-center gap-3">
                  <p className="text-sm text-gray-500">
                    {`Final times will be ${finalClockInTimeString} - ${finalClockOutTimeString}${finalBreakTimeString !== '0 mins' ? ` and a break lasting ${finalBreakTimeString}` : ''}.`}
                  </p>
                  <p className="text-sm text-gray-500">
                    {confirmationCopy.description}
                  </p>
                </div>

                <div className="mt-4 flex justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      if (
                        confirmationVariant === 'approve' &&
                        onApproveTimesheet
                      ) {
                        onApproveTimesheet(updateTimesheet.id);
                      } else if (
                        confirmationVariant === 'resolve' &&
                        onResolveTimesheet
                      ) {
                        onResolveTimesheet(updateTimesheet.id);
                      } else if (
                        confirmationVariant === 'propose' &&
                        onProposeTimesheet
                      ) {
                        onProposeTimesheet(updateTimesheet.id);
                      }
                      onClose();
                    }}
                    className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                  >
                    {confirmationCopy.action}
                  </Button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UpdateTimesheetModal;
